import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatCurrency } from '../lib/format';
import {
  Package2,
  Plus,
  Minus,
  Trash2,
  MapPin,
  Truck,
  User,
  AlertCircle,
  Check,
  ChevronRight,
  ChevronLeft,
  Search,
  Filter,
  X,
  Building2,
  Users,
  SkipForward
} from 'lucide-react';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { Product as IProduct, User as IUser } from '@bloom/shared';
import { OrderPharmacyBadge } from '../components/pharmacy/PharmacyBadge';
import { toast } from '../lib/toast';
import { OrderCutoffNotification } from '../components/common/OrderCutoffNotification';
import { AsyncSearchSelect } from '../components/common/AsyncSearchSelect';
import { useServerList, type ServerListQuery } from '../hooks/useServerList';
// Note: BulkOrderItem interface imported from shared types
interface BulkOrderItem {
  productId: string;
  product: IProduct;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

interface FulfillmentOption {
  type: 'pickup' | 'bulk_ship' | 'patient_ship';
  label: string;
  description: string;
  icon: React.ComponentType<any>;
}

const fulfillmentOptions: FulfillmentOption[] = [
  {
    type: 'pickup',
    label: 'Pharmacy Pickup',
    description: 'Pick up at pharmacy location',
    icon: MapPin
  },
  {
    type: 'bulk_ship',
    label: 'Clinic Ship',
    description: 'Ship entire order to clinic address',
    icon: Truck
  }
];

// Flat handling fee added to pharmacy-pickup bulk orders. Mirrors the backend
// PICKUP_HANDLING_FEE (stored server-side in shippingAmount). Pickups are held
// and staged manually rather than expedited.
const PICKUP_HANDLING_FEE = 20;
// Extra flat fee when an Uber courier collects a pickup order (MD-2330), on top
// of the handling fee. Mirrors the backend UBER_PICKUP_FEE.
const UBER_PICKUP_FEE = 40;

export const BulkOrders: React.FC = () => {
  const user = useUser();
  const [currentStep, setCurrentStep] = useState<'products' | 'patients' | 'fulfillment' | 'review'>('products');
  
  // Product selection state
  const [products, setProducts] = useState<IProduct[]>([]);
  const [productPrices, setProductPrices] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [bulkOrderItems, setBulkOrderItems] = useState<BulkOrderItem[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const PRODUCTS_PER_PAGE = 20;
  
  // Physician selection (for distributors and admin-on-behalf). Both pickers
  // search the server (AsyncSearchSelect): there are more AMs and physicians
  // than one page holds (MD-3130), so the chosen record is kept here rather
  // than looked up in a loaded list.
  const [selectedPhysicianId, setSelectedPhysicianId] = useState<string>('');
  const [selectedPhysician, setSelectedPhysician] = useState<IUser | null>(null);
  // Admin may select an AM first
  const [selectedAmId, setSelectedAmId] = useState<string>('');
  const [selectedAm, setSelectedAm] = useState<IUser | null>(null);
  // Every product category, for the physician branch where products are
  // paged on the server (one page's categories would hide the rest).
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const productsSeq = useRef(0);

  // Fulfillment state
  const [selectedFulfillment, setSelectedFulfillment] = useState<'pickup' | 'bulk_ship' | 'patient_ship'>('pickup');
  const [uberPickup, setUberPickup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requiresPhysicianApproval, setRequiresPhysicianApproval] = useState(false);

  // Admin "comp" — gated on adminData.canCompOrders. Zeroes every price + shipping.
  const canCompOrders = user?.role === 'admin' && user.adminData?.canCompOrders === true;
  const [compEnabled, setCompEnabled] = useState(false);
  const [compReason, setCompReason] = useState('');
  const compReasonValid = compReason.trim().length >= 5 && compReason.trim().length <= 500;

  // Auto-charge the physician's saved card on bulk submit. Only surfaced to
  // physicians placing their own order — AM/admin flows leave this off.
  const [chargeCardOnFile, setChargeCardOnFile] = useState(true);
  const [defaultCard, setDefaultCard] = useState<{ brand?: string; last4?: string } | null>(null);

  // Shipping rate state
  const [bulkShippingRate, setBulkShippingRate] = useState<number | null>(null);
  const [loadingShippingRate, setLoadingShippingRate] = useState(false);

  // Patient assignment state (physician-only, optional)
  const [patientAssignments, setPatientAssignments] = useState<Record<string, Array<{ patientId: string; patientName: string }>>>({});

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on new search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // A new category is a new question: start from its first page.
  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  }, []);

  const isPhysicianRole = user?.role === 'physician';

  // Physicians page products on the server, so their category list comes from
  // the full category metadata. The admin/AM branch loads the physician's
  // complete priced catalog (loadPhysicianPrices), so its own products are a
  // complete source.
  useEffect(() => {
    if (!isPhysicianRole) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<{ data?: { categories?: string[] } }>('/products/meta/categories');
        if (!cancelled) setAllCategories(res?.data?.categories || []);
      } catch {
        if (!cancelled) setAllCategories([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isPhysicianRole]);

  const categories = React.useMemo(() => {
    if (isPhysicianRole) return allCategories;
    const cats = new Set<string>();
    products.forEach(product => {
      product.categories.forEach((cat: string) => cats.add(cat));
    });
    return Array.from(cats).sort();
  }, [products, allCategories, isPhysicianRole]);

  // Physicians: search and category are applied by the server (loadProducts).
  // Admin/AM: loadPhysicianPrices returns the physician's complete catalog
  // (no paging), so filtering it here is safe.
  const filteredProducts = React.useMemo(() => {
    return products.filter(product => {
      const matchesCategory = isPhysicianRole || !selectedCategory || product.categories.includes(selectedCategory);
      let matchesSearch = true;
      if (!isPhysicianRole && debouncedSearchTerm) {
        const term = debouncedSearchTerm.toLowerCase();
        matchesSearch =
          (product.displayName || '').toLowerCase().includes(term) ||
          (product.drugName || '').toLowerCase().includes(term) ||
          (product.concentration || '').toLowerCase().includes(term) ||
          (product.description || '').toLowerCase().includes(term);
      }
      return matchesCategory && matchesSearch && product.isActive;
    });
  }, [products, selectedCategory, debouncedSearchTerm, isPhysicianRole]);

  // Calculate totals
  const subtotal = React.useMemo(() => {
    return bulkOrderItems.reduce((sum, item) => sum + item.totalPrice, 0);
  }, [bulkOrderItems]);

  // Format currency with commas

  const totalItems = React.useMemo(() => {
    return bulkOrderItems.reduce((sum, item) => sum + item.quantity, 0);
  }, [bulkOrderItems]);

  const loadProducts = useCallback(async (page: number = 1, search: string = '', category: string = '') => {
    const mine = ++productsSeq.current;
    try {
      setLoading(true);
      // Build query params for server-side pagination and search
      const params = new URLSearchParams({
        includePricing: 'true',
        page: String(page),
        limit: String(PRODUCTS_PER_PAGE)
      });
      if (search.trim()) {
        params.append('search', search.trim());
      }
      if (category) {
        params.append('category', category);
      }

      const response = await apiClient.get(`/products?${params.toString()}`);
      // A slower answer to an earlier search/page must not overwrite a newer one.
      if (mine !== productsSeq.current) return;
      if (response.success && response.data) {
        const loadedProducts = response.data.products || [];
        setProducts(loadedProducts);

        // Update pagination state from server response
        if (response.data.pagination) {
          setTotalProducts(response.data.pagination.total || 0);
          setTotalPages(response.data.pagination.totalPages || 1);
          setCurrentPage(response.data.pagination.page || 1);
        }

        // Extract pricing into separate state
        const prices: Record<string, number> = {};
        loadedProducts.forEach((product: any) => {
          if (product.calculatedPrice) {
            prices[product._id] = product.calculatedPrice.finalPrice;
          } else {
            prices[product._id] = product.basePrice;
          }
        });
        setProductPrices(prices);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      if (mine === productsSeq.current) setLoading(false);
    }
  }, []);

  // Initial setup based on user role
  useEffect(() => {
    if (user?.role === 'distributor' || user?.role === 'supervisor') {
      // AMs see their own physicians; supervisors see every physician in
      // their network — whether reached via an AM or invited directly. The
      // backend's getPhysicians scopes the picker's search accordingly.
      setProducts([]);
      setProductPrices({});
    } else if (user?.role === 'admin') {
      setProducts([]);
      setProductPrices({});
    }
  }, [user]);

  // Load products when search term, category or page changes (physicians only)
  useEffect(() => {
    if (user?.role === 'physician') {
      loadProducts(currentPage, debouncedSearchTerm, selectedCategory);
    }
  }, [user?.role, currentPage, debouncedSearchTerm, selectedCategory, loadProducts]);

  // Physician picker search. Admins search within the chosen AM's
  // physicians; AMs and supervisors are scoped by the backend.
  const fetchPhysicianPage = useCallback(({ page, pageSize, search, filters, signal }: ServerListQuery<{ distributorId?: string }>) =>
    apiClient.searchUsersPage({ role: 'physician', page, pageSize, search, distributorId: filters.distributorId, signal }), []);
  const fetchAccountManagerPage = useCallback(({ page, pageSize, search, signal }: ServerListQuery<Record<string, never>>) =>
    apiClient.searchUsersPage({ role: 'distributor', page, pageSize, search, signal }), []);

  const loadPhysicianPrices = async (physicianId: string) => {
    if (!physicianId) return;
    
    try {
      setLoading(true);
      
      // When admin or distributor is creating order for a physician,
      // we need to fetch the physician's specific catalog and pricing
      let responseData;
      
      if (user?.role === 'admin') {
        // Admin uses special endpoint to view physician pricing
        responseData = await apiClient.adminViewPhysicianPricing(physicianId);
      } else if (user?.role === 'distributor' || user?.role === 'supervisor') {
        // Distributor and supervisor both use the shared physician-pricing
        // endpoint (backend authorizes supervisors at routes/products.ts:1289).
        responseData = await apiClient.getPhysicianPricing(physicianId);
      }
      
      if (responseData?.success && responseData.data) {
        const productsData = responseData.data.products || [];
        // Map the products to ensure they have the correct type structure
        const typedProducts = productsData.map((p: any) => ({
          _id: p._id,
          drugName: p.drugName,
          displayName: p.displayName,
          concentration: p.concentration,
          format: p.format,
          size: p.size,
          basePrice: p.basePrice,
          categories: p.categories || [],
          description: p.description || '',
          allowPerUnitOrdering: p.allowPerUnitOrdering || false,
          images: p.images || [],
          estimatedShipDays: p.estimatedShipDays || 0,
          estimatedShipDaysRange: p.estimatedShipDaysRange || '',
          createdAt: p.createdAt,
          updatedAt: p.updatedAt,
          isActive: p.isActive !== undefined ? p.isActive : true,
          productType: p.productType || 'medication',
          directToPatient: p.directToPatient || false,
          calculatedPrice: p.calculatedPrice
        } as IProduct));
        setProducts(typedProducts);
        
        // Extract pricing into separate state
        const prices: Record<string, number> = {};
        productsData.forEach((product: any) => {
          // For admin view, use the physician's final price (what they would charge)
          // For distributor view, use the pricing they see
          if (user?.role === 'admin' && product.finalPrice !== undefined) {
            prices[product._id] = product.finalPrice;
          } else if (product.endUserPrice !== undefined) {
            prices[product._id] = product.endUserPrice;
          } else if (product.calculatedPrice) {
            prices[product._id] = product.calculatedPrice.finalPrice;
          } else {
            prices[product._id] = product.basePrice;
          }
        });
        setProductPrices(prices);
      } else {
        // If no catalog assigned, show empty products
        setProducts([]);
        setProductPrices({});
      }
    } catch (error) {
      console.error('Error loading physician prices:', error);
      // If error, show no products (physician likely has no catalog)
      setProducts([]);
      setProductPrices({});
    } finally {
      setLoading(false);
    }
  };

  // Load bulk shipping rate for the current user (physician only)
  const loadBulkShippingRate = async () => {
    // Only physicians can fetch their own shipping rate
    if (user?.role !== 'physician') {
      // For admin/distributor, we'll rely on the backend to calculate at submit time
      return;
    }

    try {
      setLoadingShippingRate(true);
      const response = await apiClient.getShippingRate(true); // true = bulk shipping rate
      if (response.success && response.data) {
        setBulkShippingRate(response.data.shippingRate);
      }
    } catch (error) {
      console.error('Failed to load bulk shipping rate:', error);
    } finally {
      setLoadingShippingRate(false);
    }
  };

  // Load bulk shipping rate when fulfillment changes to bulk_ship
  useEffect(() => {
    if (selectedFulfillment === 'bulk_ship' && user?.role === 'physician') {
      loadBulkShippingRate();
    } else if (selectedFulfillment !== 'bulk_ship') {
      setBulkShippingRate(null);
    }
  }, [selectedFulfillment, user?.role]);

  // Load the default saved card for whoever gets billed:
  //  - Physician placing for themselves → their own card
  //  - AM/admin placing for a physician → that selected physician's card
  // Re-runs when role or selected physician changes.
  useEffect(() => {
    const isPhysicianSelf = user?.role === 'physician';
    const isAmOrAdminWithTarget =
      (user?.role === 'distributor' || user?.role === 'admin' || user?.role === 'supervisor') && !!selectedPhysicianId;

    if (!isPhysicianSelf && !isAmOrAdminWithTarget) {
      setDefaultCard(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = isPhysicianSelf
          ? await apiClient.getPaymentMethods()
          : await apiClient.getPhysicianPaymentMethods(selectedPhysicianId);
        if (cancelled) return;
        if (res.success && res.data) {
          const defaultId = res.data.defaultPaymentMethodId;
          const pick = (res.data.paymentMethods || []).find(
            (pm: any) => pm.payment_method_id === defaultId
          ) || (res.data.paymentMethods || [])[0];
          if (pick?.card) {
            setDefaultCard({ brand: pick.card.card_brand, last4: pick.card.last4 });
          } else {
            setDefaultCard(null);
          }
        } else {
          setDefaultCard(null);
        }
      } catch {
        // silent — checkbox just won't appear
        setDefaultCard(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.role, selectedPhysicianId]);

  // Load physician-specific prices when distributor or admin selects a physician
  useEffect(() => {
    if ((user?.role === 'distributor' || user?.role === 'admin' || user?.role === 'supervisor') && selectedPhysicianId) {
      loadPhysicianPrices(selectedPhysicianId);
    } else if ((user?.role === 'distributor' || user?.role === 'admin' || user?.role === 'supervisor') && !selectedPhysicianId) {
      // Clear products when no physician is selected
      setProducts([]);
      setProductPrices({});
    }
  }, [selectedPhysicianId, user?.role]);

  // For admin: a different account manager means a different set of physicians
  useEffect(() => {
    if (user?.role === 'admin') {
      // A physician belongs to one AM: a new or cleared AM clears the physician.
      setSelectedPhysicianId('');
      setSelectedPhysician(null);
      // Also clear products since no physician is selected
      setProducts([]);
      setProductPrices({});
    }
  }, [selectedAmId, user?.role]);

  const physicianLabel = (p: IUser) => `Dr. ${p.profile.firstName} ${p.profile.lastName}`;
  const physicianDescription = (p: IUser) => {
    const parts = [p.profile.clinicName || p.email];
    if (p.physicianData?.npiNumber) parts.push(`NPI: ${p.physicianData.npiNumber}`);
    if (user?.role === 'supervisor') {
      const am = p.physicianData?.distributorId as any;
      parts.push(am && typeof am === 'object'
        ? `Managed by ${am.distributorData?.companyName || `${am.profile?.firstName || ''} ${am.profile?.lastName || ''}`.trim() || 'AM'}`
        : 'Direct (no AM)');
    }
    return parts.join(' • ');
  };
  const amLabel = (am: IUser) => am.distributorData?.companyName || `${am.profile.firstName} ${am.profile.lastName}`;
  const amDescription = (am: IUser) =>
    `${am.distributorData?.companyName ? `${am.profile.firstName} ${am.profile.lastName} • ` : ''}${am.email}`;
  const selectPhysician = (p: IUser | null) => {
    setSelectedPhysician(p);
    setSelectedPhysicianId(p?._id || '');
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      setBulkOrderItems(items => items.filter(item => item.productId !== productId));
      return;
    }

    setBulkOrderItems(items => {
      const existingItemIndex = items.findIndex(item => item.productId === productId);
      
      if (existingItemIndex >= 0) {
        // Update existing item with current pricing
        const updatedItems = [...items];
        const existingItem = updatedItems[existingItemIndex];
        const unitPrice = productPrices[existingItem.product._id] || existingItem.product.basePrice;
        updatedItems[existingItemIndex] = {
          ...existingItem,
          quantity,
          unitPrice,
          totalPrice: unitPrice * quantity
        };
        return updatedItems;
      } else {
        // Add new item
        const product = products.find(p => p._id === productId);
        if (!product) return items;
        
        const unitPrice = productPrices[product._id] || product.basePrice;
        const newItem: BulkOrderItem = {
          productId: product._id,
          product,
          quantity,
          unitPrice: unitPrice,
          totalPrice: unitPrice * quantity
        };
        return [...items, newItem];
      }
    });
  };

  const getItemQuantity = (productId: string): number => {
    const item = bulkOrderItems.find(item => item.productId === productId);
    return item ? item.quantity : 0;
  };

  const removeFromBulkOrder = (productId: string) => {
    setBulkOrderItems(items => items.filter(item => item.productId !== productId));
  };

  // Patient assignment helpers
  const isPhysicianUser = user?.role === 'physician';

  // The roster is searched on the server; one 500-row page left every patient
  // past it unassignable.
  const patientList = useServerList<{ _id: string; firstName: string; lastName: string; email?: string; phone: string }>({
    fetchPage: ({ page, pageSize, search, signal }) =>
      apiClient.searchPatientsPage({ page, pageSize, search, signal, sortBy: 'name', sortOrder: 'asc' }),
    pageSize: 50,
    enabled: currentStep === 'patients' && isPhysicianUser,
  });

  const isPatientAssigned = (productId: string, patientId: string): boolean => {
    return (patientAssignments[productId] || []).some(pa => pa.patientId === patientId);
  };

  const isPatientAssignedToAnyItem = (patientId: string): string | null => {
    for (const [productId, assignments] of Object.entries(patientAssignments)) {
      if (assignments.some(pa => pa.patientId === patientId)) {
        return productId;
      }
    }
    return null;
  };

  const togglePatientAssignment = (productId: string, patient: { _id: string; firstName: string; lastName: string }, maxCount: number) => {
    setPatientAssignments(prev => {
      const current = prev[productId] || [];
      const exists = current.some(pa => pa.patientId === patient._id);
      if (exists) {
        return { ...prev, [productId]: current.filter(pa => pa.patientId !== patient._id) };
      } else if (current.length < maxCount) {
        return {
          ...prev,
          [productId]: [...current, { patientId: patient._id, patientName: `${patient.firstName} ${patient.lastName}` }]
        };
      }
      return prev;
    });
  };

  const clearAllAssignments = () => {
    setPatientAssignments({});
  };

  const submitBulkOrder = async () => {
    if (bulkOrderItems.length === 0) return;
    if (!selectedFulfillment) {
      toast.warning('Please select a fulfillment method');
      return;
    }
    if ((user?.role === 'distributor' || user?.role === 'admin' || user?.role === 'supervisor') && !selectedPhysicianId) {
      toast.warning('Please select a physician for this order');
      return;
    }

    try {
      setSubmitting(true);
      
      const physicianId = (user?.role === 'distributor' || user?.role === 'admin' || user?.role === 'supervisor') ? selectedPhysicianId : user!._id;
      // Only include requiresPhysicianApproval if AM/admin is creating for a different physician
      const shouldRequireApproval = Boolean(
        requiresPhysicianApproval &&
        (user?.role === 'distributor' || user?.role === 'admin' || user?.role === 'supervisor') &&
        selectedPhysicianId
      );

      if (compEnabled && !compReasonValid) {
        toast.warning('Please enter a reason (5–500 characters) for the comp.');
        setSubmitting(false);
        return;
      }

      const bulkOrderData: any = {
        physicianId,
        items: bulkOrderItems.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          ...(patientAssignments[item.productId]?.length > 0
            ? { patientAssignments: patientAssignments[item.productId] }
            : {})
        })),
        fulfillmentDetails: {
          type: selectedFulfillment,
          uberPickup: selectedFulfillment === 'pickup' ? uberPickup : false,
          notes: 'Bulk order created via web interface'
        },
        requiresPhysicianApproval: shouldRequireApproval,
        ...(compEnabled && canCompOrders
          ? { comp: { isComped: true, reason: compReason.trim() } }
          : {})
      };

      // Create bulk order
      const createResult = await apiClient.post('/bulk-orders', bulkOrderData);
      if (!createResult.success) {
        throw new Error(createResult.error || 'Failed to create bulk order');
      }

      // Submit bulk order to create invoice (or send for approval). Pass
      // chargeCardOnFile whenever the billed physician has a card on file
      // and the placer opted in — regardless of whether the placer is the
      // physician, AM, or admin.
      const shouldChargeCard = defaultCard !== null && chargeCardOnFile;
      const submitResult = await apiClient.post(`/bulk-orders/${createResult.data._id}/submit`, {
        chargeCardOnFile: shouldChargeCard
      });
      if (!submitResult.success) {
        throw new Error(submitResult.error || 'Failed to submit bulk order');
      }

      // Show appropriate success message based on whether it needs approval
      // and whether the card-on-file was charged.
      if (submitResult.data.invoice) {
        if (submitResult.data.autoCharged) {
          toast.success(`Bulk order submitted! Invoice ${submitResult.data.invoice.invoiceNumber} — card on file charged.`);
        } else if (submitResult.data.autoChargeRequiresResave) {
          toast.warning(
            `Bulk order submitted (Invoice ${submitResult.data.invoice.invoiceNumber}), but the saved card can't be charged automatically. ` +
            `Re-add the card under Settings → Payment Methods to fix auto-pay — the invoice was emailed for manual payment.`,
            { duration: 12000 }
          );
        } else if (submitResult.data.autoChargeError) {
          toast.warning(`Bulk order submitted (Invoice ${submitResult.data.invoice.invoiceNumber}) but card-on-file charge failed: ${submitResult.data.autoChargeError}`);
        } else {
          toast.success(`Bulk order submitted successfully! Invoice ${submitResult.data.invoice.invoiceNumber} has been created.`);
        }
      } else if (submitResult.data.message) {
        toast.success(submitResult.data.message);
      } else {
        toast.success('Bulk order submitted successfully! Awaiting physician approval.');
      }

      // Reset form
      setBulkOrderItems([]);
      setSelectedFulfillment('pickup');
      setUberPickup(false);
      setSelectedPhysicianId('');
      setRequiresPhysicianApproval(false);
      setPatientAssignments({});
      setCompEnabled(false);
      setCompReason('');
      setCurrentStep('products');
      
    } catch (error: any) {
      console.error('Failed to submit bulk order:', error);
      const data = error?.response?.data;
      const serverMessage = data?.message || data?.error;
      const fallback = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to submit bulk order: ${serverMessage || fallback}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Please log in to access bulk ordering.</p>
        </div>
      </div>
    );
  }

  // Steps configuration based on user role
  const steps = useMemo(() => {
    const base = [
      { key: 'products' as const, label: 'Products', shortLabel: 'Products' },
      ...(isPhysicianUser ? [{ key: 'patients' as const, label: 'Assign Patients', shortLabel: 'Patients' }] : []),
      { key: 'fulfillment' as const, label: 'Fulfillment', shortLabel: 'Fulfillment' },
      { key: 'review' as const, label: 'Review & Submit', shortLabel: 'Review' }
    ];
    return base;
  }, [isPhysicianUser]);

  const renderStepIndicator = () => (
    <div className="mb-6 sm:mb-8">
      {/* Mobile: Horizontal compact layout */}
      <div className="sm:hidden">
        <div className="flex justify-between items-center bg-gray-50 rounded-lg p-4">
          {steps.map((s, i) => (
            <div key={s.key} className="text-center flex-1">
              <div className={`w-8 h-8 rounded-full mx-auto flex items-center justify-center ${
                currentStep === s.key ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                {i + 1}
              </div>
              <div className={`text-xs mt-1 font-medium ${currentStep === s.key ? 'text-blue-600' : 'text-gray-500'}`}>
                {s.shortLabel}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop: Horizontal layout */}
      <div className="hidden sm:flex items-center justify-center">
        <div className="flex items-center space-x-4">
          {steps.map((s, i) => (
            <React.Fragment key={s.key}>
              {i > 0 && <ChevronRight className="h-5 w-5 text-gray-400" />}
              <div className={`flex items-center ${currentStep === s.key ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep === s.key ? 'bg-blue-600 text-white' : 'bg-gray-200'
                }`}>
                  {i + 1}
                </div>
                <span className="ml-2 font-medium">{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );

  const renderOrderSummary = () => (
    <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-4 sm:mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 sm:mb-0">Order Summary</h3>
        <div className="flex items-center justify-between sm:space-x-4 text-sm text-gray-600">
          <span>{totalItems} items</span>
          <span className="text-lg font-semibold text-gray-900">{formatCurrency(subtotal)}</span>
        </div>
      </div>

      {bulkOrderItems.length > 0 && (
        <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
          <span className="text-sm text-gray-500">Filled by</span>
          <OrderPharmacyBadge
            items={bulkOrderItems.map(item => ({ isPeptide: item.product.isPeptide }))}
            size="sm"
          />
        </div>
      )}

      {/* Always reserve space for items list to prevent layout jumps */}
      <div className="space-y-2 max-h-32 overflow-y-auto min-h-[2rem]">
        {bulkOrderItems.length > 0 ? (
          bulkOrderItems.map(item => (
            <div key={item.productId} className="flex items-center justify-between text-xs sm:text-sm transition-all duration-200">
              <span className="truncate flex-1 pr-2">{item.product.displayName}</span>
              <span className="text-gray-500 mx-1 sm:mx-2 whitespace-nowrap">×{item.quantity}</span>
              <span className="font-medium whitespace-nowrap">{formatCurrency(item.totalPrice)}</span>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center text-sm text-gray-400 h-8">
            No items added yet
          </div>
        )}
      </div>
    </div>
  );

  const renderPhysicianSelection = () => (
    <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Physician</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Physician picker for distributors: searched on the server */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Choose the physician for this bulk order
          </label>
          <AsyncSearchSelect<IUser, { distributorId?: string }>
            fetchPage={fetchPhysicianPage}
            filters={{}}
            getKey={(p) => p._id}
            getLabel={physicianLabel}
            getDescription={physicianDescription}
            value={selectedPhysician}
            onChange={selectPhysician}
            placeholder="Search physicians..."
            noun="physicians"
            aria-label="Choose the physician for this bulk order"
          />
          {selectedPhysician && (
            <button onClick={() => selectPhysician(null)} className="mt-1 text-xs text-gray-500 hover:text-gray-700">
              Clear physician
            </button>
          )}
        </div>

        {/* Selected Physician Details */}
        {selectedPhysician && (
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="text-sm font-medium text-green-800 mb-2">Selected Physician Details</div>
            <div className="text-gray-900 font-medium">
              Dr. {selectedPhysician.profile.firstName} {selectedPhysician.profile.lastName}
            </div>
            <div className="text-sm text-gray-600">{selectedPhysician.email}</div>
            {user?.role === 'supervisor' && (() => {
              const am = selectedPhysician.physicianData?.distributorId as any;
              if (am && typeof am === 'object') {
                const amName = am.distributorData?.companyName
                  || `${am.profile?.firstName || ''} ${am.profile?.lastName || ''}`.trim()
                  || 'AM';
                return (
                  <div className="text-sm text-purple-700 mt-1 flex items-center">
                    <Building2 className="w-3.5 h-3.5 mr-1" />
                    Managed by {amName}
                  </div>
                );
              }
              return (
                <div className="text-sm text-gray-500 mt-1">Direct (no AM)</div>
              );
            })()}
            {selectedPhysician.physicianData?.clinicAddress && (
              <div className="text-sm text-gray-600 mt-2">
                <span className="text-gray-500">Address:</span>{' '}
                {selectedPhysician.physicianData.clinicAddress.street}
                {selectedPhysician.physicianData.clinicAddress.street2 && `, ${selectedPhysician.physicianData.clinicAddress.street2}`}, {' '}
                {selectedPhysician.physicianData.clinicAddress.city}, {' '}
                {selectedPhysician.physicianData.clinicAddress.state} {' '}
                {selectedPhysician.physicianData.clinicAddress.zipCode}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Helper function to check if physician selection is required and missing
  const isPhysicianSelectionRequired = () => {
    return (user?.role === 'admin' || user?.role === 'distributor' || user?.role === 'supervisor') && !selectedPhysicianId;
  };

  const renderPhysicianSelectionPrompt = () => (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <User className="h-8 w-8 text-blue-600" />
      </div>
      <h3 className="text-lg font-semibold text-blue-900 mb-2">
        Select a Physician First
      </h3>
      <p className="text-blue-700 mb-4">
        Please select a physician to view pricing and place orders. Pricing varies by physician based on their specific catalog and markup settings.
      </p>
      <div className="text-sm text-blue-600">
        {user?.role === 'admin' && 'Choose an Account Manager and Physician above to continue.'}
        {user?.role === 'distributor' && 'Choose a Physician from your network above to continue.'}
      </div>
    </div>
  );

  const renderProductSelection = () => (
    <div className="space-y-6">
      {/* On-behalf selection at top */}
      {user?.role === 'admin' && (
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-4 sm:mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Account Manager & Physician</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Account Manager picker: searched on the server (MD-3130) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Manager</label>
              <AsyncSearchSelect<IUser>
                fetchPage={fetchAccountManagerPage}
                getKey={(am) => am._id}
                getLabel={amLabel}
                getDescription={amDescription}
                value={selectedAm}
                onChange={(am) => { setSelectedAm(am); setSelectedAmId(am?._id || ''); }}
                placeholder="Search account managers..."
                noun="account managers"
                aria-label="Account Manager"
              />
              {selectedAm && (
                <button onClick={() => { setSelectedAm(null); setSelectedAmId(''); }} className="mt-1 text-xs text-gray-500 hover:text-gray-700">
                  Clear account manager
                </button>
              )}
            </div>

            {/* Physician picker: the chosen AM's physicians, searched on the server */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Physician</label>
              {selectedAmId ? (
                <AsyncSearchSelect<IUser, { distributorId?: string }>
                  key={selectedAmId}
                  fetchPage={fetchPhysicianPage}
                  filters={{ distributorId: selectedAmId }}
                  getKey={(p) => p._id}
                  getLabel={physicianLabel}
                  getDescription={physicianDescription}
                  value={selectedPhysician}
                  onChange={selectPhysician}
                  placeholder="Search physicians..."
                  noun="physicians"
                  aria-label="Physician"
                />
              ) : (
                <div className="w-full p-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-400">
                  Select an account manager first
                </div>
              )}
              {selectedPhysician && (
                <button onClick={() => selectPhysician(null)} className="mt-1 text-xs text-gray-500 hover:text-gray-700">
                  Clear physician
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {(user?.role === 'distributor' || user?.role === 'supervisor') && renderPhysicianSelection()}

      {/* Order Summary directly under selection */}
      {renderOrderSummary()}

      {/* Check if physician selection is required but missing */}
      {isPhysicianSelectionRequired() ? (
        renderPhysicianSelectionPrompt()
      ) : (
        <>
          {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 sm:py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
              />
            </div>
          </div>
          <div className="w-full sm:w-64">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full pl-10 pr-4 py-3 sm:py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none text-base sm:text-sm"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto mb-4"></div>
            Loading products...
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center text-gray-500">
            {debouncedSearchTerm ? (
              <>
                <p className="mb-2">No products found matching "{debouncedSearchTerm}"</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-blue-600 hover:underline text-sm"
                >
                  Clear search
                </button>
              </>
            ) : (
              'No products found'
            )}
          </div>
        ) : (
          <>
            {/* Product count display */}
            <div className="flex items-center justify-between text-sm text-gray-600 px-1">
              <span>
                {totalProducts > 0 && (
                  debouncedSearchTerm
                    ? `Found ${totalProducts} product${totalProducts !== 1 ? 's' : ''} matching "${debouncedSearchTerm}"`
                    : `${totalProducts} product${totalProducts !== 1 ? 's' : ''} available`
                )}
              </span>
              {selectedCategory && (
                <span className="flex items-center">
                  Filtered by: <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">{selectedCategory}</span>
                  <button
                    onClick={() => handleCategoryChange('')}
                    className="ml-1 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>

            {/* Desktop: Table view */}
            <div className="hidden lg:block bg-white rounded-lg shadow-sm border overflow-hidden data-table-container">
              <div className="data-table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Category</th>
                      <th>Unit Price</th>
                      <th>Quantity</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map(product => {
                      const quantity = getItemQuantity(product._id);
                      const unitPrice = productPrices[product._id] || product.basePrice;
                      const total = quantity * unitPrice;
                      const isOOS = (product as any).outOfStock === true;
                      const isLowStock = (product as any).lowStockWarning === true;

                      return (
                        <tr key={product._id} className={isOOS ? 'bg-red-50' : isLowStock ? 'bg-amber-50' : ''}>
                          <td className="cell-primary">
                            <div className="cell-stack">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="cell-stack-value">{product.displayName}</span>
                                {isOOS && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                    Out of Stock
                                  </span>
                                )}
                                {isLowStock && !isOOS && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                    Low Stock
                                  </span>
                                )}
                              </div>
                              <span className="cell-stack-label">{product.drugName} - {product.concentration}</span>
                              {isLowStock && !isOOS && (
                                <span className="text-xs text-amber-700 mt-0.5">
                                  Low stock, please confirm availability for on time fulfillment
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="cell-secondary">
                            {product.categories[0] || 'Uncategorized'}
                          </td>
                          <td className="cell-mono">
                            {formatCurrency(unitPrice)}
                          </td>
                          <td className="cell-secondary">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => updateQuantity(product._id, Math.max(0, quantity - 1))}
                                disabled={quantity === 0 || isOOS}
                                className="p-1 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <input
                                type="number"
                                defaultValue={quantity}
                                key={`desktop-qty-${product._id}-${quantity}`}
                                onChange={(e) => { if (isOOS) return; const v = parseInt(e.target.value); if (!isNaN(v)) updateQuantity(product._id, Math.max(0, v)); }}
                                onBlur={(e) => { if (isOOS) { e.target.value = '0'; return; } const v = parseInt(e.target.value); const c = isNaN(v) || v < 0 ? 0 : v; updateQuantity(product._id, c); e.target.value = String(c); }}
                                min="0"
                                disabled={isOOS}
                                className="w-16 text-center py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                              />
                              <button
                                onClick={() => updateQuantity(product._id, quantity + 1)}
                                disabled={isOOS}
                                className="p-1 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => removeFromBulkOrder(product._id)}
                                disabled={quantity === 0}
                                className={`p-1 rounded-md transition-all duration-200 ${
                                  quantity > 0
                                    ? 'text-red-600 hover:bg-red-50 opacity-100'
                                    : 'text-transparent cursor-default opacity-0'
                                }`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                          <td className="cell-mono font-medium">
                            {quantity > 0 ? formatCurrency(total) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile/Tablet: Card view */}
            <div className="lg:hidden space-y-3">
              {filteredProducts.map(product => {
                const quantity = getItemQuantity(product._id);
                const unitPrice = productPrices[product._id] || product.basePrice;
                const total = quantity * unitPrice;
                const isOOS = (product as any).outOfStock === true;
                const isLowStock = (product as any).lowStockWarning === true;

                return (
                  <div key={product._id} className={`bg-white rounded-lg shadow-sm border p-4 ${isOOS ? 'border-red-300 bg-red-50' : isLowStock ? 'border-amber-300 bg-amber-50' : ''}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 pr-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium text-gray-900 text-sm sm:text-base">
                            {product.displayName}
                          </h3>
                          {isOOS && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                              Out of Stock
                            </span>
                          )}
                          {isLowStock && !isOOS && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                              Low Stock
                            </span>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 mt-1">
                          {product.drugName} - {product.concentration}
                        </p>
                        {isLowStock && !isOOS && (
                          <p className="text-xs text-amber-700 mt-1">
                            Low stock, please confirm availability for on time fulfillment
                          </p>
                        )}
                        <div className="flex items-center mt-2 space-x-4 text-xs sm:text-sm">
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                            {product.categories[0] || 'Uncategorized'}
                          </span>
                          <span className="font-medium text-gray-900">
                            {formatCurrency(unitPrice)}
                          </span>
                        </div>
                      </div>
                      {quantity > 0 && (
                        <button
                          onClick={() => removeFromBulkOrder(product._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors touch-manipulation"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => updateQuantity(product._id, Math.max(0, quantity - 1))}
                          disabled={quantity === 0 || isOOS}
                          className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <input
                          type="number"
                          min="0"
                          defaultValue={quantity}
                          key={`mobile-qty-${product._id}-${quantity}`}
                          onChange={(e) => { if (isOOS) return; const v = parseInt(e.target.value); if (!isNaN(v)) updateQuantity(product._id, Math.max(0, v)); }}
                          onBlur={(e) => { if (isOOS) { e.target.value = '0'; return; } const v = parseInt(e.target.value); const c = isNaN(v) || v < 0 ? 0 : v; updateQuantity(product._id, c); e.target.value = String(c); }}
                          disabled={isOOS}
                          className="w-16 text-center border border-gray-300 rounded-md py-2 text-sm touch-manipulation disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <button
                          onClick={() => updateQuantity(product._id, quantity + 1)}
                          disabled={isOOS}
                          className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {quantity > 0 && (
                        <div className="text-right">
                          <div className="text-sm text-gray-500">Total</div>
                          <div className="font-semibold text-gray-900">{formatCurrency(total)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Showing {((currentPage - 1) * PRODUCTS_PER_PAGE) + 1}-{Math.min(currentPage * PRODUCTS_PER_PAGE, totalProducts)} of {totalProducts} products
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1 || loading}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </button>
              <div className="flex items-center space-x-1">
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      disabled={loading}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      } disabled:opacity-50`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages || loading}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}

          {/* Next Button */}
          <div className="flex justify-end">
            <button
              onClick={() => setCurrentStep(isPhysicianUser ? 'patients' : 'fulfillment')}
              disabled={bulkOrderItems.length === 0 || isPhysicianSelectionRequired()}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <span>Next: {isPhysicianUser ? 'Assign Patients' : 'Fulfillment'}</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderPatientAssignment = () => {
    const nonSupplyItems = bulkOrderItems.filter(item => {
      const prod = item.product as any;
      return !prod?.isSupply && prod?.productType !== 'supply';
    });

    return (
      <div className="space-y-6">
        {/* Info banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Users className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-base font-semibold text-blue-900">Assign Patients (Optional)</h3>
              <p className="text-sm text-blue-700 mt-1">
                Optionally assign patients from your roster to each product. When assigned, real patient data will be sent to pharmacy.
              </p>
            </div>
          </div>
        </div>

        {/* Patient search */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search patients by name, email, or phone..."
              value={patientList.search}
              onChange={(e) => patientList.setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          {patientList.loading && (
            <div className="text-sm text-gray-500 mt-2">Loading patients...</div>
          )}
          {!patientList.loading && !patientList.search && patientList.total === 0 && (
            <div className="text-sm text-gray-500 mt-2">
              No patients found. <span className="text-blue-600">Import patients via CSV from your Patient Roster first.</span>
            </div>
          )}
          {!patientList.loading && patientList.total !== null && patientList.total > patientList.items.length && (
            <div className="text-xs text-gray-500 mt-2">
              Showing {patientList.items.length} of {patientList.total} patients. Search to find others.
            </div>
          )}
        </div>

        {/* Per-item patient assignment */}
        {nonSupplyItems.map(item => {
          const assigned = patientAssignments[item.productId] || [];
          const isComplete = assigned.length === item.quantity;
          const hasPartial = assigned.length > 0 && !isComplete;

          return (
            <div key={item.productId} className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className={`p-4 border-b ${isComplete ? 'bg-green-50 border-green-200' : hasPartial ? 'bg-amber-50 border-amber-200' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">{item.product.displayName}</h4>
                    <p className="text-sm text-gray-500">{item.product.drugName} - Qty: {item.quantity}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${
                      isComplete ? 'text-green-700' : hasPartial ? 'text-amber-700' : 'text-gray-500'
                    }`}>
                      {assigned.length} / {item.quantity} assigned
                    </span>
                    {isComplete && <Check className="h-4 w-4 text-green-600" />}
                    {assigned.length > 0 && (
                      <button
                        onClick={() => setPatientAssignments(prev => ({ ...prev, [item.productId]: [] }))}
                        className="text-xs text-gray-500 hover:text-red-500 ml-2"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Patient checklist */}
              <div className="max-h-60 overflow-y-auto divide-y divide-gray-100">
                {patientList.items.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    {patientList.search ? 'No patients match your search' : 'No patients available'}
                  </div>
                ) : (
                  patientList.items.map(patient => {
                    const isAssignedHere = isPatientAssigned(item.productId, patient._id);
                    const otherItemProductId = !isAssignedHere ? isPatientAssignedToAnyItem(patient._id) : null;
                    const otherItem = otherItemProductId ? bulkOrderItems.find(i => i.productId === otherItemProductId) : null;
                    const isDisabled = !isAssignedHere && (assigned.length >= item.quantity || !!otherItemProductId);

                    return (
                      <label
                        key={patient._id}
                        className={`flex items-center p-3 transition-colors cursor-pointer ${
                          isAssignedHere ? 'bg-blue-50' : isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isAssignedHere}
                          onChange={() => {
                            if (!isDisabled || isAssignedHere) {
                              togglePatientAssignment(item.productId, patient, item.quantity);
                            }
                          }}
                          disabled={isDisabled && !isAssignedHere}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="ml-3 flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            {patient.firstName} {patient.lastName}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {patient.email && <span>{patient.email}</span>}
                            {patient.email && patient.phone && <span> &middot; </span>}
                            {patient.phone && <span>{patient.phone}</span>}
                          </div>
                        </div>
                        {otherItem && (
                          <span className="text-xs text-amber-600 ml-2 whitespace-nowrap">
                            Assigned to {otherItem.product.displayName}
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={() => setCurrentStep('products')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Back: Products
          </button>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => { clearAllAssignments(); setCurrentStep('fulfillment'); }}
              className="px-4 py-2 border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50 flex items-center space-x-2"
            >
              <SkipForward className="h-4 w-4" />
              <span>Skip</span>
            </button>
            <button
              onClick={() => setCurrentStep('fulfillment')}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <span>Next: Fulfillment</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFulfillmentSelection = () => {
    const currentPhysician = user?.role === 'distributor' ? selectedPhysician : user;
    
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Fulfillment Method</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {fulfillmentOptions.map(option => {
              const Icon = option.icon;
              return (
                <div
                  key={option.type}
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedFulfillment === option.type
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedFulfillment(option.type)}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className={`h-6 w-6 ${
                      selectedFulfillment === option.type ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{option.label}</div>
                      <div className="text-sm text-gray-500">{option.description}</div>
                      {option.type === 'bulk_ship' && currentPhysician?.physicianData?.clinicAddress && (
                        <div className="text-xs text-gray-600 mt-1 p-2 bg-gray-50 rounded">
                          <div className="font-medium">Ship to:</div>
                          <div>
                            {currentPhysician.physicianData.clinicAddress.street}<br/>
                            {currentPhysician.physicianData.clinicAddress.street2 && <>{currentPhysician.physicianData.clinicAddress.street2}<br/></>}
                            {currentPhysician.physicianData.clinicAddress.city}, {currentPhysician.physicianData.clinicAddress.state} {currentPhysician.physicianData.clinicAddress.zipCode}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pickup is staged manually, not expedited — warn before the user commits. */}
          {selectedFulfillment === 'pickup' && (
            <div className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium text-amber-900">Pickups are not expedited</p>
                <p className="mt-0.5">
                  Pickup orders are held and staged manually, so they may take many hours to be
                  ready and can miss the same-day pharmacy cutoff. A flat {formatCurrency(PICKUP_HANDLING_FEE)} handling
                  fee applies.
                </p>
              </div>
            </div>
          )}

          {/* Uber-courier pickup tag (MD-2330) — pickup only */}
          {selectedFulfillment === 'pickup' && (
            <label className="mt-3 flex items-start gap-3 border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={uberPickup}
                onChange={(e) => setUberPickup(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">Uber courier pickup (+{formatCurrency(UBER_PICKUP_FEE)})</span>
                <span className="block text-gray-500 mt-0.5">
                  Flags the order so the pharmacy hands it to an Uber courier. Only available for clinics
                  within 50 miles of the pharmacy.
                </span>
              </span>
            </label>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={() => setCurrentStep(isPhysicianUser ? 'patients' : 'products')}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
        >
          Back: {isPhysicianUser ? 'Patients' : 'Products'}
        </button>
        <button
          onClick={() => setCurrentStep('review')}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
        >
          <span>Next: Review</span>
          <ChevronRight className="h-4 w-4" />
        </button>
        </div>
      </div>
    );
  };

  const renderReviewSubmit = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Your Order</h3>
        
        {/* Order Items */}
        <div className="space-y-3 mb-6">
          {bulkOrderItems.map(item => {
            const itemAssignments = patientAssignments[item.productId] || [];
            const isOOS = (item.product as any).outOfStock === true;
            return (
              <div key={item.productId} className={`py-3 border-b border-gray-100 last:border-b-0 ${isOOS ? 'bg-red-50 -mx-2 px-2 rounded' : ''}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{item.product.displayName}</span>
                      {isOOS && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          Out of Stock
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{item.product.drugName}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Qty: {item.quantity}</div>
                    <div className="font-medium">{formatCurrency(item.totalPrice)}</div>
                  </div>
                  {isOOS && (
                    <button
                      onClick={() => removeFromBulkOrder(item.productId)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-md"
                      title="Remove out-of-stock item"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {itemAssignments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    <span className="text-xs text-gray-500 mr-1">Patients:</span>
                    {itemAssignments.map(pa => (
                      <span key={pa.patientId} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                        {pa.patientName}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {bulkOrderItems.some(item => (item.product as any).outOfStock === true) && (
          <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>One or more items are out of stock. Remove them before submitting.</span>
          </div>
        )}

        {/* Fulfillment Method */}
        <div className="bg-gray-50 rounded-md p-4 mb-6">
          <div className="text-sm font-medium text-gray-700 mb-1">Fulfillment Method</div>
          <div className="text-gray-900">
            {fulfillmentOptions.find(opt => opt.type === selectedFulfillment)?.label}
          </div>
          {selectedFulfillment === 'pickup' && (
            <div className="mt-2 flex items-start gap-2 text-xs text-amber-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                Not expedited — held and staged manually, so it may take many hours and can miss
                the same-day cutoff. Includes a {formatCurrency(PICKUP_HANDLING_FEE)} handling fee.
              </span>
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="border-t pt-4 space-y-3">
          {/* Subtotal */}
          <div className="flex justify-between items-center text-gray-700">
            <span>Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>

          {/* Shipping - only for bulk_ship fulfillment */}
          {selectedFulfillment === 'bulk_ship' && (
            <div className="flex justify-between items-center text-gray-700">
              <span>Shipping</span>
              {loadingShippingRate ? (
                <span className="text-gray-400">Loading...</span>
              ) : bulkShippingRate !== null ? (
                <span>{formatCurrency(bulkShippingRate)}</span>
              ) : (
                <span className="text-gray-400">Calculated at checkout</span>
              )}
            </div>
          )}

          {/* Handling fee - flat charge on pickup orders (comp orders are $0) */}
          {!compEnabled && selectedFulfillment === 'pickup' && (
            <div className="flex justify-between items-center text-gray-700">
              <span>Handling fee</span>
              <span>{formatCurrency(PICKUP_HANDLING_FEE)}</span>
            </div>
          )}

          {/* Uber courier fee - flat charge when Uber pickup is selected */}
          {!compEnabled && selectedFulfillment === 'pickup' && uberPickup && (
            <div className="flex justify-between items-center text-gray-700">
              <span>Uber courier fee</span>
              <span>{formatCurrency(UBER_PICKUP_FEE)}</span>
            </div>
          )}

          {/* Total */}
          <div className="flex justify-between items-center text-lg font-semibold pt-2 border-t">
            <span>Total Amount</span>
            <span>
              {compEnabled
                ? <span className="text-pink-700">{formatCurrency(0)} <span className="text-xs font-normal">(comped)</span></span>
                : selectedFulfillment === 'bulk_ship' && bulkShippingRate !== null
                  ? formatCurrency(subtotal + bulkShippingRate)
                  : selectedFulfillment === 'pickup'
                    ? formatCurrency(subtotal + PICKUP_HANDLING_FEE + (uberPickup ? UBER_PICKUP_FEE : 0))
                    : formatCurrency(subtotal)}
            </span>
          </div>

          <div className="text-sm text-gray-600 mt-1">
            {compEnabled
              ? 'This order is comped — a $0 paid invoice will be created and no payouts will attach. The order still ships normally.'
              : requiresPhysicianApproval
                ? 'This order will be sent to the physician for review and approval before creating an invoice.'
                : 'An invoice will be generated and sent for payment approval'}
          </div>
        </div>

        {/* Auto-charge card on file — physician placing for themselves, OR
            AM/admin placing for a physician who has a card on file. Suppressed
            for comp orders since there's nothing to charge. */}
        {defaultCard && !compEnabled && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={chargeCardOnFile}
                onChange={(e) => setChargeCardOnFile(e.target.checked)}
                className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <div>
                <span className="font-medium text-gray-900">
                  Charge {defaultCard.brand ? `${defaultCard.brand.charAt(0).toUpperCase() + defaultCard.brand.slice(1)} ` : 'card '}
                  ending in {defaultCard.last4 || '••••'}
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  Your saved card will be charged automatically when this bulk order is submitted. Uncheck to pay the invoice manually instead.
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Admin comp panel — only visible to admins with canCompOrders, after a
            physician has been picked. Mutually exclusive with charge-card-on-file
            and physician-approval (a comp ships immediately, costs nothing). */}
        {canCompOrders && selectedPhysicianId && (
          <div className="mt-6 bg-pink-50 border border-pink-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={compEnabled}
                onChange={(e) => {
                  setCompEnabled(e.target.checked);
                  if (e.target.checked) {
                    setRequiresPhysicianApproval(false);
                    setChargeCardOnFile(false);
                  }
                }}
                className="mt-1 w-5 h-5 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900">Comp this order ($0, no payouts)</span>
                <p className="text-sm text-gray-600 mt-1">
                  Every item price, markup, and shipping fee is set to $0. A paid $0 invoice is
                  created and the order ships normally. No revenue or payouts will attach.
                </p>
                {compEnabled && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={compReason}
                      onChange={(e) => setCompReason(e.target.value)}
                      rows={2}
                      maxLength={500}
                      placeholder="e.g. Damaged in transit replacement, sample for clinical trial, marketing comp"
                      className="w-full px-3 py-2 border border-pink-300 rounded-md text-sm focus:ring-pink-500 focus:border-pink-500"
                    />
                    <div className={`text-xs mt-1 ${compReasonValid ? 'text-gray-500' : 'text-red-600'}`}>
                      {compReason.trim().length}/500 — 5 character minimum
                    </div>
                  </div>
                )}
              </div>
            </label>
          </div>
        )}

        {/* Physician Approval Toggle - only show when AM/admin creates for a physician */}
        {(user?.role === 'distributor' || user?.role === 'admin' || user?.role === 'supervisor') && selectedPhysicianId && !compEnabled && (
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresPhysicianApproval}
                onChange={(e) => setRequiresPhysicianApproval(e.target.checked)}
                className="mt-1 w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
              />
              <div>
                <span className="font-medium text-gray-900">Requires Physician Approval</span>
                <p className="text-sm text-gray-600 mt-1">
                  When enabled, the physician will need to review and approve this order before it becomes an invoice.
                  They can also modify the order before approving.
                </p>
              </div>
            </label>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={() => setCurrentStep('fulfillment')}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
        >
          Back: Fulfillment
        </button>
        <button
          onClick={submitBulkOrder}
          disabled={submitting || (compEnabled && !compReasonValid) || bulkOrderItems.some(item => (item.product as any).outOfStock === true)}
          title={bulkOrderItems.some(item => (item.product as any).outOfStock === true) ? 'Remove out-of-stock items before submitting' : undefined}
          className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              <span>Submit Order</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
          <Package2 className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3 text-blue-600" />
          Bulk Orders
        </h1>
        <p className="mt-2 text-sm sm:text-base text-gray-600">
          Create bulk orders for regular supplies with quantity-based pricing and flexible fulfillment options.
        </p>
        {/* Order Cutoff Notification */}
        <OrderCutoffNotification variant="compact" className="mt-4" />
      </div>

      {renderStepIndicator()}
      {currentStep === 'products' && renderProductSelection()}
      {currentStep === 'patients' && renderPatientAssignment()}
      {currentStep === 'fulfillment' && renderFulfillmentSelection()}
      {currentStep === 'review' && renderReviewSubmit()}
    </div>
  );
};

export default BulkOrders;